import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Tag, Star, Clock, Percent, PoundSterling, Truck } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { useNavigate } from 'react-router-dom'
import { useCartStore } from '@/stores/cartStore'
import { Product } from '@/types'

const productTypeLabels: Record<string, string> = {
  sim_only: 'SIM Only',
  broadband: 'Broadband',
  bundle: 'Bundle',
  router: 'Router',
  hardware: 'Hardware',
}

interface PublicOffer {
  id: number
  title: string
  description: string
  discount_type: 'percentage' | 'fixed_amount' | 'free_shipping' | 'buy_x_get_y'
  discount_value: number
  code: string
  valid_from: string
  valid_until: string
  terms: string[]
  featured: boolean
  category: string
  minimum_purchase: number
}

type TabType = 'products' | 'codes'

export default function SpecialOffers() {
  const navigate = useNavigate()
  const { addItem } = useCartStore()
  const [activeTab, setActiveTab] = useState<TabType>('products')
  const [addedProduct, setAddedProduct] = useState<number | null>(null)
  const [filterType, setFilterType] = useState<string>('all')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [copiedCode, setCopiedCode] = useState<string | null>(null)

  // Fetch special offer products
  const { data: productsData, isLoading: productsLoading } = useQuery<{ products: Product[]; count: number }>({
    queryKey: ['special-offer-products'],
    queryFn: async () => {
      const res = await fetch('/api/products/?is_special_offer=true')
      if (!res.ok) throw new Error('Failed to fetch')
      return res.json()
    },
  })

  // Fetch public discount offers
  const { data: discountOffers = [], isLoading: offersLoading } = useQuery<PublicOffer[]>({
    queryKey: ['public-discount-offers', selectedCategory],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (selectedCategory !== 'all') params.append('category', selectedCategory)
      const res = await fetch(`/api/discounts/offers/public?${params}`)
      if (!res.ok) throw new Error('Failed to fetch')
      return res.json()
    },
  })

  // Fetch spin wheel teaser
  const { data: spinWheelData } = useQuery({
    queryKey: ['spin-wheel-public'],
    queryFn: async () => {
      const res = await fetch('/api/spin-wheel/public')
      if (!res.ok) return null
      return res.json()
    },
    retry: false,
  })
  const showSpinTeaser = spinWheelData?.visible === true

  const allProducts = productsData?.products || []
  const filteredProducts = filterType === 'all' ? allProducts : allProducts.filter(p => p.product_type === filterType)
  const availableTypes = Array.from(new Set(allProducts.map(p => p.product_type)))

  const handleAddToCart = (product: Product) => {
    addItem(product)
    setAddedProduct(product.id)
    setTimeout(() => setAddedProduct(null), 2000)
  }

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code)
      setTimeout(() => setCopiedCode(null), 2000)
    })
  }

  const formatDiscount = (offer: PublicOffer) => {
    switch (offer.discount_type) {
      case 'percentage': return `${offer.discount_value}% OFF`
      case 'fixed_amount': return `£${offer.discount_value} OFF`
      case 'free_shipping': return 'FREE SHIPPING'
      case 'buy_x_get_y': return 'BUY X GET Y'
      default: return `${offer.discount_value}% OFF`
    }
  }

  const DiscountIcon = ({ type }: { type: string }) => {
    if (type === 'percentage') return <Percent className="w-5 h-5 text-orange-600" />
    if (type === 'fixed_amount') return <PoundSterling className="w-5 h-5 text-orange-600" />
    if (type === 'free_shipping') return <Truck className="w-5 h-5 text-orange-600" />
    return <Tag className="w-5 h-5 text-orange-600" />
  }

  const daysLeft = (validUntil: string) =>
    Math.ceil((new Date(validUntil).getTime() - Date.now()) / 86400000)

  const formatData = (product: Product) => {
    if (product.data_allowance_gb === -1) return 'Unlimited Data'
    if (product.data_allowance_gb) return `${product.data_allowance_gb}${(product as any).data_unit || 'GB'} Data`
    return null
  }

  const categoryButtons = [
    { id: 'all', label: 'All Offers' },
    { id: 'new_customer', label: 'New Customer' },
    { id: 'seasonal', label: 'Seasonal' },
    { id: 'promotional', label: 'Promotional' },
    { id: 'loyalty', label: 'Loyalty' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-pink-50 to-primary-50">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-orange-600 via-pink-600 to-primary-500 text-white">
        <div className="absolute inset-0 bg-grid-white/10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="inline-flex p-4 bg-white/20 backdrop-blur-sm rounded-2xl mb-6">
            <Tag className="w-16 h-16" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Special Offers</h1>
          <p className="text-2xl text-orange-100 max-w-3xl mx-auto">
            Exclusive deals — limited time only
          </p>
        </div>
      </div>

      {/* ── Spin & Win Teaser ── */}
      {showSpinTeaser && (
        <div className="bg-gradient-to-r from-green-900 via-green-800 to-green-900 border-y border-green-700/40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
            <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8">

              {/* Mini decorative wheel — hidden on xs, shown sm+ */}
              <div className="hidden sm:flex flex-shrink-0 items-center justify-center">
                <div className="relative w-28 h-28 sm:w-32 sm:h-32">
                  <div className="absolute inset-0 rounded-full bg-green-400/10 blur-xl animate-pulse" />
                  <div className="absolute inset-0 rounded-full overflow-hidden border-4 border-white/20">
                    {(spinWheelData?.prizes || []).map((_: any, i: number, arr: any[]) => (
                      <div
                        key={i}
                        className="absolute inset-0 origin-center"
                        style={{
                          transform: `rotate(${(i / arr.length) * 360}deg)`,
                          background: `conic-gradient(${_.color || '#40A345'} 0deg, ${_.color || '#40A345'} ${360 / arr.length}deg, transparent ${360 / arr.length}deg)`,
                        }}
                      />
                    ))}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-10 h-10 bg-green-950 rounded-full border-2 border-white/20 flex items-center justify-center text-lg">
                        🔒
                      </div>
                    </div>
                  </div>
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0"
                    style={{ borderLeft: '7px solid transparent', borderRight: '7px solid transparent', borderTop: '16px solid #fbbf24' }} />
                </div>
              </div>

              {/* Main text */}
              <div className="flex-1 text-center sm:text-left min-w-0">
                <div className="inline-block bg-amber-400 text-amber-900 text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full mb-3">
                  {spinWheelData?.name || 'Special Promotion'}
                </div>
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-black text-white mb-2 whitespace-nowrap overflow-hidden text-ellipsis">
                  {spinWheelData?.heading || 'Spin to Save!'}
                </h2>
                <p className="text-green-200 mb-4 text-sm">
                  {spinWheelData?.subheading || 'Log in or register to spin the wheel and win a real discount on your order!'}{' '}
                  Codes valid for {spinWheelData?.code_validity_days || 5} days.
                </p>
                <div className="flex flex-col xs:flex-row sm:flex-row gap-2 sm:gap-3 justify-center sm:justify-start">
                  <button
                    onClick={() => navigate('/register')}
                    className="px-5 py-2.5 bg-amber-400 hover:bg-amber-300 text-amber-900 font-black rounded-xl text-sm transition-colors shadow-lg whitespace-nowrap"
                  >
                    🎉 Register Free &amp; Spin!
                  </button>
                  <button
                    onClick={() => navigate('/login')}
                    className="px-5 py-2.5 border border-green-400/50 text-green-200 hover:bg-green-700/40 font-bold rounded-xl text-sm transition-colors whitespace-nowrap"
                  >
                    Login to Spin →
                  </button>
                </div>
              </div>

              {/* Decorative "Spin & Win!" — single line, desktop only */}
              <div className="hidden lg:block flex-shrink-0 text-right">
                <p className="text-3xl xl:text-4xl font-black whitespace-nowrap">
                  <span className="text-white">Spin</span>
                  <span className="text-green-400"> &amp; Win!</span>
                </p>
                <p className="text-xs text-green-400/70 mt-1">Members only</p>
              </div>

            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* Tab Switcher */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex bg-white rounded-xl shadow-md p-1">
            <button
              onClick={() => setActiveTab('products')}
              className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'products'
                  ? 'bg-gradient-to-r from-orange-600 to-pink-600 text-white shadow'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Special Offer Plans
              {allProducts.length > 0 && (
                <span className="ml-2 text-xs bg-white/20 px-1.5 py-0.5 rounded-full">
                  {activeTab === 'products' ? <span className="text-white">{allProducts.length}</span> : <span className="text-orange-600">{allProducts.length}</span>}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('codes')}
              className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'codes'
                  ? 'bg-gradient-to-r from-orange-600 to-pink-600 text-white shadow'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Discount Codes
              {discountOffers.length > 0 && (
                <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">
                  {discountOffers.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* TAB: Special Offer Products */}
        {activeTab === 'products' && (
          <>
            {/* Type filter */}
            {availableTypes.length > 1 && (
              <div className="flex flex-wrap justify-center gap-3 mb-10">
                {['all', ...availableTypes].map(type => (
                  <button
                    key={type}
                    onClick={() => setFilterType(type)}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold transition-all ${
                      filterType === type
                        ? 'bg-gradient-to-r from-orange-600 to-pink-600 text-white shadow-lg scale-105'
                        : 'bg-white text-gray-700 hover:shadow-md'
                    }`}
                  >
                    <Star className="w-4 h-4" />
                    {type === 'all' ? 'All Plans' : productTypeLabels[type] || type}
                  </button>
                ))}
              </div>
            )}

            {productsLoading ? (
              <div className="text-center py-20 text-gray-500">Loading special offers...</div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-gray-500 text-lg">No special offer plans available right now. Check back soon!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProducts.map(product => {
                  const dataInfo = formatData(product)
                  return (
                    <Card
                      key={product.id}
                      className="relative overflow-hidden bg-white/90 shadow-xl hover:shadow-2xl transition-all hover:scale-[1.02] ring-4 ring-orange-400"
                    >
                      <div className="absolute top-0 right-0 bg-gradient-to-r from-orange-600 to-pink-600 text-white px-4 py-1 text-sm font-bold rounded-bl-xl">
                        SPECIAL OFFER
                      </div>
                      <div className="p-6">
                        {product.image_url && (
                          <div className="flex justify-center mb-4">
                            <img src={product.image_url} alt={product.name} className="h-16 object-contain" />
                          </div>
                        )}
                        <div className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 rounded-full text-xs font-semibold text-orange-700 mb-3">
                          <Tag className="w-3 h-3" />
                          {productTypeLabels[product.product_type] || product.product_type}
                          {product.network_type && ` · ${product.network_type.toUpperCase()}`}
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{product.name}</h3>
                        {(dataInfo || product.minutes || product.texts) && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {dataInfo && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">{dataInfo}</span>}
                            {product.minutes && <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">{product.minutes} Mins</span>}
                            {product.texts && <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">{product.texts} Texts</span>}
                          </div>
                        )}
                        {product.features && product.features.length > 0 && (
                          <ul className="space-y-1 mb-4">
                            {product.features.slice(0, 3).map((f, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                                <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                {f}
                              </li>
                            ))}
                          </ul>
                        )}
                        <div className="mb-4">
                          {product.discounted_price ? (
                            <div className="flex items-center gap-2">
                              <span className="text-2xl font-bold text-orange-600">£{product.discounted_price.toFixed(2)}</span>
                              <span className="text-lg text-gray-400 line-through">£{product.price.toFixed(2)}</span>
                              {product.is_contract && <span className="text-sm text-gray-500">/mo</span>}
                            </div>
                          ) : (
                            <div className="flex items-baseline gap-1">
                              <span className="text-2xl font-bold text-gray-900">£{product.price.toFixed(2)}</span>
                              {product.is_contract && <span className="text-sm text-gray-500">/mo</span>}
                            </div>
                          )}
                          {product.is_contract && product.contract_months && (
                            <p className="text-xs text-gray-500 mt-0.5">{product.contract_months === 1 ? "rolling monthly contract" : `${product.contract_months}-month contract`}</p>
                          )}
                        </div>
                        <button
                          onClick={() => handleAddToCart(product)}
                          className={`w-full py-3 px-6 rounded-xl font-semibold transition-all ${
                            addedProduct === product.id
                              ? 'bg-green-500 text-white'
                              : 'bg-gradient-to-r from-orange-600 to-pink-600 hover:from-orange-700 hover:to-pink-700 text-white shadow-lg'
                          }`}
                        >
                          {addedProduct === product.id ? '✓ Added to Cart' : 'Add to Cart'}
                        </button>
                      </div>
                    </Card>
                  )
                })}
              </div>
            )}
          </>
        )}

        {/* TAB: Discount Codes */}
        {activeTab === 'codes' && (
          <>
            {/* Category filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              {categoryButtons.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-5 py-2.5 rounded-xl font-semibold transition-all ${
                    selectedCategory === cat.id
                      ? 'bg-gradient-to-r from-orange-600 to-pink-600 text-white shadow-lg scale-105'
                      : 'bg-white text-gray-700 hover:shadow-md'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {offersLoading ? (
              <div className="text-center py-20 text-gray-500">Loading offers...</div>
            ) : discountOffers.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-gray-500 text-lg">No discount offers available in this category</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {discountOffers.map(offer => (
                  <Card
                    key={offer.id}
                    className={`relative overflow-hidden bg-white/90 shadow-xl hover:shadow-2xl transition-all hover:scale-[1.02] ${
                      offer.featured ? 'ring-4 ring-orange-500' : ''
                    }`}
                  >
                    {offer.featured && (
                      <div className="absolute top-0 right-0 bg-gradient-to-r from-orange-600 to-pink-600 text-white px-4 py-1 text-sm font-bold rounded-bl-xl">
                        FEATURED
                      </div>
                    )}
                    <div className="p-6">
                      {/* Discount Badge */}
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-100 to-pink-100 rounded-xl mb-4">
                        <DiscountIcon type={offer.discount_type} />
                        <span className="font-bold text-orange-600 text-xl">{formatDiscount(offer)}</span>
                      </div>

                      <h3 className="text-xl font-bold text-gray-900 mb-2">{offer.title}</h3>
                      <p className="text-gray-600 mb-4 text-sm">{offer.description}</p>

                      {/* Promo Code Box */}
                      <div
                        className="mb-4 p-3 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 cursor-pointer hover:border-orange-400 transition-colors"
                        onClick={() => handleCopyCode(offer.code)}
                        title="Click to copy"
                      >
                        <div className="text-xs text-gray-500 mb-1">Promo Code — click to copy</div>
                        <div className="flex items-center justify-between">
                          <span className="font-mono font-bold text-lg text-gray-900">{offer.code}</span>
                          {copiedCode === offer.code ? (
                            <span className="text-xs text-green-600 font-semibold">✓ Copied!</span>
                          ) : (
                            <span className="text-xs text-gray-400">Copy</span>
                          )}
                        </div>
                      </div>

                      {offer.minimum_purchase > 0 && (
                        <p className="text-xs text-gray-500 mb-3">Minimum purchase: £{offer.minimum_purchase}</p>
                      )}

                      {/* Expiry */}
                      <div className="flex items-center gap-2 mb-4 text-sm">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className={daysLeft(offer.valid_until) <= 7 ? 'text-red-600 font-medium' : 'text-gray-500'}>
                          {daysLeft(offer.valid_until) > 0
                            ? `Expires in ${daysLeft(offer.valid_until)} days`
                            : 'Expired'}
                        </span>
                      </div>

                      {/* Terms */}
                      {offer.terms && offer.terms.length > 0 && (
                        <div className="mb-5">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Terms & Conditions:</p>
                          <ul className="space-y-1">
                            {offer.terms.map((term, i) => (
                              <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                                <span className="text-orange-500 mt-0.5">•</span>
                                {term}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <button
                        onClick={() => navigate('/sim-only')}
                        className="w-full py-3 px-6 rounded-xl font-semibold bg-gradient-to-r from-orange-600 to-pink-600 hover:from-orange-700 hover:to-pink-700 text-white shadow-lg transition-all"
                      >
                        Browse Plans
                      </button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* Bottom CTA */}
        <Card className="mt-16 bg-gradient-to-r from-orange-50 to-pink-50 border-orange-200 p-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Can't find what you're looking for?</h2>
          <p className="text-gray-600 mb-6 text-lg">Contact our sales team for exclusive custom deals</p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => navigate('/contact')}
              className="px-8 py-4 rounded-xl font-semibold bg-gradient-to-r from-orange-600 to-pink-600 hover:from-orange-700 hover:to-pink-700 text-white shadow-lg"
            >
              Contact Sales
            </button>
            <button
              onClick={() => navigate('/sim-only')}
              className="px-8 py-4 rounded-xl font-semibold bg-white text-gray-900 hover:bg-gray-50 shadow-lg border-2 border-gray-200"
            >
              Browse All Plans
            </button>
          </div>
        </Card>
      </div>
    </div>
  )
}
